import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { inr } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { buildWhatsappLink, track } from "../lib/analytics";
import ProductCard from "../components/ProductCard";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Heart, MessageCircle, ShoppingBag, Truck, RefreshCcw, Leaf } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [activeImg, setActiveImg] = useState(0);
    const [size, setSize] = useState("");
    const [pincode, setPincode] = useState("");
    const [pinResult, setPinResult] = useState(null);
    const [pinChecking, setPinChecking] = useState(false);
    const { settings } = useSettings();
    const { has, toggle } = useWishlist();
    const { add } = useCart();
    const loadGeneration = useRef(0);

    useEffect(() => {
        loadGeneration.current += 1;
        const generation = loadGeneration.current;
        api.get(`/products/${slug}`).then(({ data }) => {
            if (generation !== loadGeneration.current) return;
            setProduct(data);
            setSize(data.sizes?.[0] || "");
            setActiveImg(0);
            document.title = `${data.seoTitle || data.name} | Loomara`;
            track("view_item", {
                item_id: data.id, item_name: data.name, item_category: data.category, price: data.price,
            });
            api.get("/products", { params: { category: data.category, limit: 4 } }).then(({ data: rel }) => {
                if (generation !== loadGeneration.current) return;
                setRelated(rel.filter((r) => r.id !== data.id).slice(0, 4));
            });
        }).catch(() => {
            if (generation === loadGeneration.current) setProduct(false);
        });
        return () => {
            loadGeneration.current += 1;
        };
    }, [slug]);

    if (product === null) {
        return <div className="container-boutique py-24 text-center text-ink-500" data-testid="product-loading">Loading…</div>;
    }
    if (product === false) {
        return <div className="container-boutique py-24 text-center" data-testid="product-not-found">
            <p className="font-serif text-3xl">We couldn't find that piece</p>
            <Link to="/shop" className="btn-primary mt-6">Back to Shop</Link>
        </div>;
    }

    const liked = has(product.id);
    const oos = product.inStock === false || product.stockStatus === "out_of_stock";

    const onAddToCart = () => {
        if (oos) { toast("This piece is currently out of stock."); return; }
        if ((product.sizes?.length > 0) && !size) { toast("Please choose a size first."); return; }
        add(product, { size });
        track("buy_now_click", { item_id: product.id, item_name: product.name, item_category: product.category, price: product.price });
    };

    const checkPincode = async () => {
        if (!/^\d{6}$/.test(pincode)) { toast("Enter a valid 6-digit pincode."); return; }
        setPinChecking(true);
        try {
            const { data } = await api.get("/shipping/serviceability", { params: { pincode } });
            setPinResult(data);
        } catch (e) {
            setPinResult({ serviceable: false, error: e.response?.data?.detail || "Check failed" });
        } finally { setPinChecking(false); }
    };

    const onWhatsapp = () => {
        track("whatsapp_click", {
            item_id: product.id, item_name: product.name, item_category: product.category, price: product.price,
        });
        const url = buildWhatsappLink({
            number: settings?.whatsappNumber || "919999999999",
            template: product.whatsappMessageTemplate || settings?.defaultWhatsappTemplate,
            product,
        });
        window.open(url, "_blank", "noopener");
    };

    return (
        <div data-testid="product-detail-page">
            <div className="container-boutique py-10">
                <nav className="text-xs uppercase tracking-[0.22em] text-ink-500 mb-8">
                    <Link to="/" className="hover:text-sage-600">Home</Link>
                    <span className="mx-2">/</span>
                    <Link to={`/category/${product.category}`} className="hover:text-sage-600">{product.category}</Link>
                    <span className="mx-2">/</span>
                    <span className="text-ink-900">{product.name}</span>
                </nav>

                <div className="grid lg:grid-cols-12 gap-12">
                    {/* Gallery */}
                    <div className="lg:col-span-7">
                        <div className="rounded-[2rem] overflow-hidden bg-ivory-100 aspect-[4/5]">
                            <img src={product.images?.[activeImg]} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="mt-4 flex gap-3">
                            {product.images?.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImg(i)}
                                    className={`w-20 h-24 rounded-xl overflow-hidden bg-ivory-100 border-2 transition-colors ${i === activeImg ? "border-sage-500" : "border-transparent"}`}
                                    data-testid={`thumb-${i}`}
                                >
                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="lg:col-span-5">
                        <p className="eyebrow">Loomara · {product.category}</p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-ink-900 mt-3 leading-tight">{product.name}</h1>
                        {product.subtitle && <p className="text-ink-500 italic font-serif text-lg mt-2">{product.subtitle}</p>}

                        <div className="mt-6 flex items-baseline gap-3">
                            <span className="text-2xl text-sage-700 font-medium">{inr(product.price)}</span>
                            {product.compareAtPrice && product.compareAtPrice > product.price && (
                                <span className="text-base text-ink-500 line-through">{inr(product.compareAtPrice)}</span>
                            )}
                        </div>

                        <p className="text-ink-700 mt-6 leading-relaxed">{product.shortDescription}</p>

                        {/* Size selector */}
                        {product.sizes?.length > 0 && (
                            <div className="mt-8" data-testid="size-selector">
                                <p className="text-xs uppercase tracking-[0.22em] text-ink-900 mb-3">Select size</p>
                                <div className="flex flex-wrap gap-2">
                                    {product.sizes.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setSize(s)}
                                            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider border transition-colors ${size === s ? "bg-sage-400 text-white border-sage-400" : "bg-white text-ink-900 border-sage-300/40 hover:border-sage-500"}`}
                                            data-testid={`size-${s}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* CTAs */}
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <button onClick={onAddToCart} disabled={oos} className={`flex-1 ${oos ? "bg-ivory-100 text-ink-500 cursor-not-allowed rounded-full px-8 py-3 text-xs uppercase tracking-[0.18em]" : "btn-primary"}`} data-testid="buy-now-btn">
                                <ShoppingBag size={14} /> {oos ? "Sold Out" : "Add to Bag"}
                            </button>
                            <button onClick={onWhatsapp} className="btn-secondary flex-1" data-testid="whatsapp-pdp-btn">
                                <MessageCircle size={14} /> Inquire on WhatsApp
                            </button>
                            <button
                                onClick={() => toggle(product)}
                                className={`h-12 w-12 rounded-full border border-sage-300/40 flex items-center justify-center transition-colors ${liked ? "text-blush-500" : "text-ink-700"} hover:text-blush-500`}
                                aria-label="Wishlist"
                                data-testid="pdp-wishlist-btn"
                            >
                                <Heart size={16} strokeWidth={1.5} fill={liked ? "currentColor" : "none"} />
                            </button>
                        </div>

                        {/* Pincode delivery check */}
                        <div className="mt-6 p-4 rounded-2xl bg-ivory-100 border border-sage-300/30" data-testid="pincode-checker">
                            <p className="text-xs uppercase tracking-[0.22em] text-ink-900 mb-2">Check delivery time</p>
                            <div className="flex gap-2">
                                <input
                                    value={pincode}
                                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    placeholder="6-digit pincode"
                                    className="flex-1 bg-white rounded-full px-4 py-2 text-sm border border-sage-300/40 focus:outline-none focus:ring-2 focus:ring-sage-300"
                                    data-testid="pincode-input"
                                />
                                <button onClick={checkPincode} disabled={pinChecking} className="bg-sage-400 hover:bg-sage-500 text-white rounded-full px-5 text-xs uppercase tracking-[0.18em] transition-colors" data-testid="pincode-check-btn">
                                    {pinChecking ? "Checking…" : "Check"}
                                </button>
                            </div>
                            {pinResult && (
                                <p className={`text-xs mt-2 ${pinResult.serviceable ? "text-sage-700" : "text-blush-500"}`} data-testid="pincode-result">
                                    {pinResult.serviceable
                                        ? `✓ Delivers to ${pincode} in ~${pinResult.estimated_days || 5} days`
                                        : (pinResult.error || `We don't currently deliver to ${pincode}`)}
                                </p>
                            )}
                        </div>

                        {/* Notes */}
                        <div className="grid grid-cols-3 gap-3 mt-8 text-[11px] uppercase tracking-[0.18em] text-ink-500">
                            <div className="flex flex-col items-center text-center py-3 px-2 bg-ivory-100 rounded-2xl">
                                <Leaf size={16} className="text-sage-600 mb-2" /> {product.fabric ? "Premium Fabric" : "Soft Fabric"}
                            </div>
                            <div className="flex flex-col items-center text-center py-3 px-2 bg-ivory-100 rounded-2xl">
                                <Truck size={16} className="text-sage-600 mb-2" /> Free Shipping ₹1499+
                            </div>
                            <div className="flex flex-col items-center text-center py-3 px-2 bg-ivory-100 rounded-2xl">
                                <RefreshCcw size={16} className="text-sage-600 mb-2" /> Easy Returns
                            </div>
                        </div>

                        {/* Accordion */}
                        <Accordion type="single" collapsible className="mt-8" defaultValue="desc">
                            <AccordionItem value="desc">
                                <AccordionTrigger className="text-xs uppercase tracking-[0.22em]" data-testid="acc-description">Description</AccordionTrigger>
                                <AccordionContent className="text-ink-700 leading-relaxed">{product.longDescription}</AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="fabric">
                                <AccordionTrigger className="text-xs uppercase tracking-[0.22em]" data-testid="acc-fabric">Fabric & Care</AccordionTrigger>
                                <AccordionContent className="text-ink-700 leading-relaxed">
                                    <p><strong className="text-ink-900">Fabric:</strong> {product.fabric}</p>
                                    <p className="mt-2"><strong className="text-ink-900">Care:</strong> {product.careInstructions}</p>
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="size">
                                <AccordionTrigger className="text-xs uppercase tracking-[0.22em]" data-testid="acc-size">Size & Age Guidance</AccordionTrigger>
                                <AccordionContent className="text-ink-700 leading-relaxed">
                                    Recommended for ages: {product.ageGroup?.join(", ")}. Available sizes: {product.sizes?.join(", ")}.
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="ship">
                                <AccordionTrigger className="text-xs uppercase tracking-[0.22em]" data-testid="acc-ship">Shipping & Returns</AccordionTrigger>
                                <AccordionContent className="text-ink-700 leading-relaxed">
                                    <p>{product.shippingNote}</p>
                                    <p className="mt-2">{product.returnNote}</p>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>
                </div>

                {/* Related */}
                {related.length > 0 && (
                    <section className="mt-24">
                        <h2 className="font-serif text-3xl sm:text-4xl text-ink-900 text-center mb-10">You may also love</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="related-products">
                            {related.map((p, i) => (<ProductCard key={p.id} product={p} index={i} listName="related" />))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
